// src/pages/Aura.jsx
import React from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/layout/Navbar';
import '../styles/PageTopIntro.css';
import '../styles/Aura.css';
import Footerbar from '../components/layout/Footerbar';
import PageTopIntroLine from '../components/layout/PageTopIntroLine';
import ARBackground from '../components/animations/ARBackground';
import { useWindowScrollProgressRef } from '../hooks/useWindowScrollProgressRef';
import {
  sectionStagger,
  fadeUpItem,
  revealViewport,
} from '../components/animations/animationVariants';

const AURA_USE_CASE_TAGS = [
  '갤러리·미술관 전시',
  '박물관·역사관 전시',
  '브랜드 팝업스토어',
  '브랜드 쇼룸',
  '페스티벌·이벤트',
  '박람회·마켓',
  '제품 체험·데모',
  '마케팅 캠페인',
  '옷가게·패션 매장',
  '카페·레스토랑·F&B',
  '서점·화장품 등 그 외 소매',
  '미술 교육·워크숍',
];

const Aura = () => {
  const bgScrollRef = useWindowScrollProgressRef();
  const IntroLine = PageTopIntroLine;

  return (
    <>
      <ARBackground scrollProgressRef={bgScrollRef} />
      <Navbar />
      <header className="page-top-intro">
        <IntroLine text="WebAR·통합 운영·AI 인사이트를 한 흐름으로 — Aura" />
      </header>

      {/* ========== 1. Overview ========== */}
      <section className="aura-body">
        <motion.header
          className="aura-body-hero"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <span className="section-eyebrow">01 / Overview</span>
          <h2 className="aura-body-hero__title">
            <span className="highlight">Aura</span>
            <em className="aura-body-hero__dot">.</em>
          </h2>
          <p className="aura-body-hero__tag">
            시선을 사로잡는 경험, 성장을 이끄는 데이터
          </p>
        </motion.header>

        <motion.div
          className="aura-body-lede"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <p className="aura-body-lede__text">
            눈으로만 보던 전시를 넘어, 제품과 컬렉션에 관람객이 직접 참여하고 몰입하도록 돕습니다.
            Aura는 오프라인 공간에 AR을 결합해 이목을 사로잡고, 현장의 즉각적인 반응을 데이터로
            분석해 공간 기획의 해답을 제시하는 클라우드 솔루션입니다.
          </p>
        </motion.div>
      </section>

      {/* ========== 2. Capabilities ========== */}
      <section className="aura-features">
        <motion.div
          className="aura-features-head"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <span className="section-eyebrow">02 / Capabilities</span>
          <h2 className="features-title">
            전시와 매장에 필요한 세 가지 축
          </h2>
          <p className="aura-features-lead">
            기획부터 배포, 데이터 분석까지. Aura는 한 플랫폼 안에서 공간 경험을 설계하고 운영하는
            모든 흐름을 다룹니다.
          </p>
        </motion.div>

        <motion.div
          className="feature-block"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <div className="feature-block__head">
            <span className="feature-block__num">01</span>
            <h3>코딩 없는 직관적 AR 스튜디오</h3>
          </div>
          <ul>
            <li>손쉬운 에셋 추가 — 코딩 없이 3D·이미지 에셋을 즉시 업로드하고 배치</li>
            <li>정밀한 제어 — 슬라이더로 X·Y·Z 위치, 크기, 회전을 완전히 통제</li>
            <li>원클릭 이펙트 — 이펙트와 애니메이션 프리셋을 바로 적용</li>
            <li>실시간 프리뷰 — 모바일 결과를 작업 캔버스에서 즉시 검증</li>
          </ul>
        </motion.div>

        <motion.div
          className="feature-block"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <div className="feature-block__head">
            <span className="feature-block__num">02</span>
            <h3>장벽 없는 Seamless WebAR 배포</h3>
          </div>
          <ul>
            <li>앱 설치 Zero — 웹 브라우저에서 바로 실행</li>
            <li>초간편 배포 — 자동 생성되는 링크와 QR 코드로 현장에 공유</li>
            <li>유연한 AR 환경 — 이미지 인식과 공간 인식을 모두 지원</li>
            <li>최고의 참여율 — 기술적 장벽을 없애 참여를 극대화</li>
          </ul>
        </motion.div>

        <motion.div
          className="feature-block"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <div className="feature-block__head">
            <span className="feature-block__num">03</span>
            <h3>KIRI AI 기반 데이터 컨설팅</h3>
          </div>
          <ul>
            <li>핵심 지표 수집 — 체류 시간, 좋아요, 다시보기 등 참여 데이터 기록</li>
            <li>관람 패턴 분석 — 컬렉션 성격 자동 분류와 트렌드 감지</li>
            <li>컬렉션 시너지 — 함께 감상할 때 반응이 좋은 작품 간의 &apos;궁합&apos; 분석</li>
            <li>데이터 기반 기획 — 선호 태그와 피크 시간대 기반의 맞춤 리포트</li>
          </ul>
        </motion.div>
      </section>

      {/* ========== 3. Use Cases ========== */}
      <section className="target-impact">
        <motion.div
          className="target-impact-head"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <span className="section-eyebrow">03 / Use Cases</span>
          <h2 className="target-impact-main-title">
            오프라인 경험의 새로운 기준이 필요한 곳에
          </h2>
          <p className="target-impact-intro">
            전시, 팝업, 매장 — 관람객과 만나는 모든 현장에 Aura가 함께합니다. 참여를 이끌고
            그 반응을 데이터로 분석해, 더 나은 공간 기획으로 이어지도록 돕습니다.
          </p>
        </motion.div>

        <motion.h3
          className="target-impact-use-heading"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          Aura가 만드는 공간의 변화
        </motion.h3>

       <motion.div
         className="target-row"
         variants={fadeUpItem}
         initial="hidden"
         whileInView="visible"
         viewport={revealViewport}
       >
         <img src="/image/gallery.png" alt="전시·갤러리" className="target-photo" />
         <div className="target-text">
           <p className="target-persona-kicker">전시 · 갤러리</p>
           <h3 className="target-title">작품의 세계관을 공간으로 확장한 큐레이터</h3>
           <p>
             <strong>고민</strong>
             정적인 전시 특성상 관람객이 눈으로만 훑고 지나가 체류 시간이 짧고,
             기획 의도를 온전히 전달하기 어려웠습니다.
           </p>
           <p>
             <strong>도입</strong>
             컬렉션을 스캔하면 작품 고유의 분위기에 맞춘 3D 오브젝트와 파티클 이펙트가 등장해,
             물리적 작품과 AR이 &apos;하나의 완성된 컬렉션&apos;으로 경험되도록 세팅했습니다.
           </p>
           <p>
             <strong>활용</strong>
             키리가 수집한 컬렉션별 체류 시간 데이터를 통해 관람객이 가장 깊게 몰입한 요소를
             파악하고, 차기 전시의 작품 배치 기준으로 활용했습니다.
           </p>
         </div>
       </motion.div>

       <motion.div
         className="target-row reverse"
         variants={fadeUpItem}
         initial="hidden"
         whileInView="visible"
         viewport={revealViewport}
       >
         <img src="/image/brand.png" alt="브랜드 팝업" className="target-photo" />
         <div className="target-text">
           <p className="target-persona-kicker">브랜드 팝업</p>
           <h3 className="target-title">고객의 숨겨진 니즈를 데이터로 찾은 마케터</h3>
           <p>
             <strong>고민</strong>
             팝업스토어에 여러 신제품을 전시했지만, 어떤 제품 라인에 관심이 모이고
             어떻게 묶어 프로모션해야 할지 객관적인 기준이 없었습니다.
           </p>
           <p>
             <strong>도입</strong>
             주요 매대마다 핵심 성분과 브랜드 스토리를 담은 3D AR 콘텐츠를 세팅해,
             오프라인에서 제품을 탐색하는 과정을 디지털 데이터로 남기도록 유도했습니다.
           </p>
           <p>
             <strong>활용</strong>
             키리의 &apos;컬렉션 시너지 분석&apos; 리포트에서 A 스킨케어와 C 향수 매대를
             함께 경험한 고객의 상호작용이 가장 높다는 점을 확인하고, 두 제품을 묶은
             현장 세트 프로모션을 기획했습니다.
           </p>
         </div>
       </motion.div>

       <motion.div
         className="target-row"
         variants={fadeUpItem}
         initial="hidden"
         whileInView="visible"
         viewport={revealViewport}
       >
         <img src="/image/artist.png" alt="에이전시·기관" className="target-photo" />
         <div className="target-text">
           <p className="target-persona-kicker">에이전시 · 기관</p>
           <h3 className="target-title">다중 공간 운영과 성과를 증명한 디렉터</h3>
           <p>
             <strong>고민</strong>
             지자체·기업 행사를 외주로 진행하면 비용이 높고, 행사가 끝난 뒤
             객관적인 정량 성과 보고서를 만들기 어려웠습니다.
           </p>
           <p>
             <strong>도입</strong>
             내부 기획팀이 에디터로 3D 로고의 위치와 크기를 직접 세팅하고, 행사 당일
             클라이언트의 변경 요청도 현장에서 즉시 수정해 재배포했습니다.
           </p>
           <p>
             <strong>활용</strong>
             행사 종료 후 키리가 자동 분석한 &apos;구역별 관람객 상호작용·체류 시간 리포트&apos;를
             클라이언트 결과 보고서에 그대로 제출해, 데이터 기반의 객관적 성과를 증명했습니다.
           </p>
         </div>
       </motion.div>

       <motion.div
         className="target-use-cases"
         variants={fadeUpItem}
         initial="hidden"
         whileInView="visible"
         viewport={revealViewport}
       >
         <h3 className="target-use-cases-title">어떤 형태의 공간이든 유연하게</h3>
         <p className="target-use-cases-subtext">
           규모나 업종과 관계없이, 브랜드와 관람객이 만나는 접점이라면 어디든
           Aura를 적용할 수 있습니다.
         </p>
         <div className="target-use-cases-marquee">
           <div className="target-use-cases-marquee-track">
             <ul className="target-use-case-list" aria-label="적용 가능한 공간 유형">
               {AURA_USE_CASE_TAGS.map((label) => (
                 <li key={label}>{label}</li>
               ))}
             </ul>
             <ul className="target-use-case-list" aria-hidden="true">
               {AURA_USE_CASE_TAGS.map((label) => (
                 <li key={`${label}-dup`}>{label}</li>
               ))}
             </ul>
           </div>
         </div>
       </motion.div>
     </section>

      {/* ========== 4. Workflow ========== */}
      <section className="how-it-works">
        <motion.div
          className="how-it-works-head"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <span className="section-eyebrow">04 / Workflow</span>
          <h2 className="how-it-works-title">
            기획부터 분석까지, 네 단계로 완성되는 현장 경험
          </h2>
          <p className="how-it-works-lead">
            복잡한 제작 과정을 간소화했습니다. 한 흐름 안에서 콘텐츠를 만들고 배포하고 데이터로 돌아봅니다.
          </p>
        </motion.div>

        <motion.div
          className="steps-grid"
          variants={sectionStagger}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <motion.div className="step-block" variants={fadeUpItem}>
            <span className="step-block__num">01</span>
            <img src="/image/drawing.svg" alt="콘텐츠 기획" />
            <h3 className="step-title">콘텐츠 기획</h3>
            <p className="step-desc">마커와 스토리를 설계</p>
          </motion.div>

          <motion.div className="step-block" variants={fadeUpItem}>
            <span className="step-block__num">02</span>
            <img src="/image/writing.svg" alt="통합 운영 등록" />
            <h3 className="step-title">통합 운영에 등록</h3>
            <p className="step-desc">에셋 업로드·연동</p>
          </motion.div>

          <motion.div className="step-block" variants={fadeUpItem}>
            <span className="step-block__num">03</span>
            <img src="/image/qr-code.svg" alt="QR 배포" />
            <h3 className="step-title">QR 코드 배포</h3>
            <p className="step-desc">링크·QR로 현장에 공유</p>
          </motion.div>

          <motion.div className="step-block" variants={fadeUpItem}>
            <span className="step-block__num">04</span>
            <img src="/image/bar-chart.svg" alt="데이터 분석" />
            <h3 className="step-title">데이터 분석·활용</h3>
            <p className="step-desc">로그 시각화와 피드백</p>
          </motion.div>
        </motion.div>

        <motion.div
          className="cta-button"
          variants={fadeUpItem}
          initial="hidden"
          whileInView="visible"
          viewport={revealViewport}
        >
          <a
            href="https://cms.biiyeon.com"
            target="_blank"
            rel="noreferrer"
            className="cta-button__link"
          >
            AURA 접속하기
          </a>
        </motion.div>
      </section>

      <Footerbar />
    </>
  );
};

export default Aura;
